import React, {
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  useImperativeHandle,
  useMemo,
  JSX
} from 'react';
import useFixedElementScroll from '../../Hooks/useFixedElementScroll';
import useConfig from '../../Hooks/useConfig';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../Hooks/useMediaQuery';

const FixedElementScroll = forwardRef(
  (
    props: PropsWithChildren<{
      offset?: number;
      width?: number;
    }>,
    ref?: ForwardedRef<{ reEvaluate: () => void }>
  ): JSX.Element => {
    const { children, offset, width = 25 } = props;
    const config = useConfig();
    const { stickyElementsOffset } = config;
    const isTablet = useMediaQuery({
      type: 'min',
      query: stickyElementsOffset?.tablet?.breakpoint?.toString() || MEDIAQUERY_DEFAULTS.md
    });
    const isDesktop = useMediaQuery({
      type: 'min',
      query: stickyElementsOffset?.desktop?.breakpoint?.toString() || MEDIAQUERY_DEFAULTS.lg
    });

    const getOffset = useMemo((): number => {
      let newOffset = 0;
      if (!!stickyElementsOffset) {
        if (isTablet && !isDesktop) {
          newOffset = stickyElementsOffset.tablet.offset;
        } else if (isDesktop) {
          newOffset = stickyElementsOffset.desktop.offset;
        } else {
          newOffset = stickyElementsOffset.mobile.offset;
        }
      }
      return newOffset;
    }, [stickyElementsOffset, isTablet, isDesktop]);

    const { containerRef, elementRef, onScroll, onResize } = useFixedElementScroll(
      offset || getOffset
    );

    useImperativeHandle(ref, () => ({
      reEvaluate: () => {
        onScroll();
        onResize();
      }
    }));

    return (
      <div
        ref={containerRef}
        className="fixed-scroll__container"
        style={{ flex: `0 0 ${width}%`, maxWidth: `${width}%`, width: `${width}%` }}
      >
        <div ref={elementRef} className="fixed-scroll__element">
          {children}
        </div>
      </div>
    );
  }
);

export default FixedElementScroll;
